import { useEffect, useState } from 'react';
import { Box, Divider, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { RawProductUsage, RawProductUsageEdit } from 'services/rawProductService';
import { usePressToSave } from './usePressToSave';

interface Props {
  // Every procedure the product holds, or null when nothing is open. All of them
  // at once because the answer is the whole list: removing one is a thing only a
  // complete list can say.
  usages: RawProductUsage[] | null;
  open: boolean;
  productName: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (usages: RawProductUsageEdit[]) => void;
}

// A procedure as it is being edited. sourceIndex is where it came from in the
// stored list, and it is what carries the plan behind it - the intent, the
// areas, the components, the machine-readable steps - across a save that only
// touches the words. A step is one row here even though the two languages are
// stored as separate lists: they are the same sequence said twice, and editing
// them apart is how a fifth English step ends up beside a fourth Arabic one.
interface UsageDraft {
  sourceIndex: number;
  nameEn: string;
  nameAr: string;
  steps: { en: string; ar: string }[];
}

function toDraft(usage: RawProductUsage, index: number): UsageDraft {
  const en = usage.instructions?.en ?? [];
  const ar = usage.instructions?.ar ?? [];
  const rows = Math.max(en.length, ar.length);

  return {
    sourceIndex: index,
    nameEn: usage.name?.en ?? '',
    nameAr: usage.name?.ar ?? '',
    steps: Array.from({ length: rows }, (_value, row) => ({
      en: en[row] ?? '',
      ar: ar[row] ?? '',
    })),
  };
}

export default function UsageModal({
  usages,
  open,
  productName,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [drafts, setDrafts] = useState<UsageDraft[]>([]);
  const { saving, press } = usePressToSave(isSaving, open);

  // Opened on what the row holds every time rather than on whatever was left
  // here last: an abandoned edit that came back on the next open would read as a
  // stored procedure.
  useEffect(() => {
    if (!open) return;
    setDrafts((usages ?? []).map(toDraft));
  }, [open, usages]);

  const stored = JSON.stringify((usages ?? []).map(toDraft));
  const changed = JSON.stringify(drafts) !== stored;

  const patch = (index: number, next: Partial<UsageDraft>) =>
    setDrafts((existing) =>
      existing.map((draft, position) => (position === index ? { ...draft, ...next } : draft)),
    );

  // A step moved with its translation, for the same reason a step is deleted
  // with it: the two lists are one sequence written twice, and moving one side
  // alone is how a fourth English step ends up beside a third Arabic one.
  const moveStep = (index: number, row: number, to: number) =>
    setDrafts((existing) =>
      existing.map((draft, position) => {
        if (position !== index) return draft;
        if (to < 0 || to >= draft.steps.length) return draft;

        const steps = [...draft.steps];
        const [moved] = steps.splice(row, 1);
        steps.splice(to, 0, moved);
        return { ...draft, steps };
      }),
    );

  const patchStep = (index: number, row: number, next: Partial<{ en: string; ar: string }>) =>
    patch(index, {
      steps: drafts[index].steps.map((step, position) =>
        position === row ? { ...step, ...next } : step,
      ),
    });

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 900,
          maxWidth: '94vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <CustomText text="Edit usages" fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        {drafts.length === 0 && (
          <Box mt={2}>
            <CustomText
              text="This product holds no procedures. Saving now leaves it that way."
              fontSize={13}
              color={colors.gray}
            />
          </Box>
        )}

        {drafts.map((draft, index) => (
          <Box key={`usage-draft-${draft.sourceIndex}`} mt={2.5}>
            {index > 0 && <Divider sx={{ mb: 2.5 }} />}

            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <CustomText
                text={`Procedure ${index + 1}`}
                fontSize={14}
                fontWeight={600}
                color={colors.primary}
              />
              {/* The whole procedure, not a step: a product read as having two
                  when it has one is corrected by dropping the wrong one. */}
              <Tooltip title="Remove this procedure">
                <IconButton
                  size="small"
                  disabled={saving}
                  onClick={() =>
                    setDrafts((existing) => existing.filter((_draft, position) => position !== index))
                  }
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* The two languages side by side in the arrangement the card uses:
                the Arabic is the same sequence written for its own reader, so
                what is worth seeing while correcting either is the row where
                they stop matching. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1.5,
              }}
            >
              <Stack spacing={0.75}>
                <CustomText text="Name (EN)" fontSize={12} fontWeight={600} color={colors.gray} />
                <TextField
                  fullWidth
                  size="small"
                  value={draft.nameEn}
                  disabled={saving}
                  onChange={(event) => patch(index, { nameEn: event.target.value })}
                  placeholder="—"
                />
              </Stack>
              <Stack spacing={0.75}>
                <CustomText text="Name (AR)" fontSize={12} fontWeight={600} color={colors.gray} />
                <TextField
                  fullWidth
                  size="small"
                  value={draft.nameAr}
                  disabled={saving}
                  onChange={(event) => patch(index, { nameAr: event.target.value })}
                  placeholder="—"
                  inputProps={{ dir: 'rtl' }}
                />
              </Stack>
            </Box>

            {draft.steps.map((step, row) => (
              <Box
                key={`usage-${draft.sourceIndex}-step-${row}`}
                mt={1.5}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
                  columnGap: 1.5,
                  rowGap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <Stack spacing={0.5}>
                  <CustomText
                    text={`EN ${row + 1}`}
                    fontSize={11}
                    fontWeight={600}
                    color={colors.gray}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={step.en}
                    disabled={saving}
                    onChange={(event) => patchStep(index, row, { en: event.target.value })}
                    placeholder="—"
                  />
                </Stack>
                <Stack spacing={0.5}>
                  <CustomText
                    text={`AR ${row + 1}`}
                    fontSize={11}
                    fontWeight={600}
                    color={colors.gray}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={step.ar}
                    disabled={saving}
                    onChange={(event) => patchStep(index, row, { ar: event.target.value })}
                    placeholder="—"
                    inputProps={{ dir: 'rtl' }}
                  />
                </Stack>
                {/* The step in both languages at once: they are one step, and a
                    row removed from one side and left on the other is how the
                    two sequences stop lining up. */}
                <Stack direction="row" alignItems="center" sx={{ mt: 2.25 }}>
                  {/* Where the step goes in the sequence, which is part of the
                      answer: a pack that says to rinse before it says to lather
                      was read in the wrong order, not in the wrong words. */}
                  <Tooltip title="Move this step up">
                    <span>
                      <IconButton
                        size="small"
                        disabled={saving || row === 0}
                        onClick={() => moveStep(index, row, row - 1)}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 16, color: colors.gray }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move this step down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={saving || row === draft.steps.length - 1}
                        onClick={() => moveStep(index, row, row + 1)}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 16, color: colors.gray }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove this step">
                    <span>
                      <IconButton
                        size="small"
                        disabled={saving}
                        onClick={() =>
                          patch(index, {
                            steps: draft.steps.filter((_step, position) => position !== row),
                          })
                        }
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            ))}

            <Box mt={1}>
              <CustomButton
                text="Add step"
                variant="outlined"
                height={32}
                icon={<AddIcon sx={{ fontSize: 16 }} />}
                disabled={saving}
                onClick={() => patch(index, { steps: [...draft.steps, { en: '', ar: '' }] })}
              />
            </Box>
          </Box>
        ))}

        <Box mt={2}>
          <CustomText
            text="The steps are in the order they are performed. The plan behind each procedure - what it is for, where it acts, what it is done with - is kept as it was read; only these words change."
            fontSize={11}
            color={colors.gray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={saving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={saving}
            disabled={!changed}
            onClick={() => press(() => onSave(
                drafts.map((draft) => ({
                  sourceIndex: draft.sourceIndex,
                  nameEn: draft.nameEn.trim(),
                  nameAr: draft.nameAr.trim(),
                  // A step blank in both languages is one the reviewer emptied
                  // rather than one they wrote, so it is not sent.
                  instructionsEn: draft.steps
                    .filter((step) => step.en.trim() || step.ar.trim())
                    .map((step) => step.en.trim()),
                  instructionsAr: draft.steps
                    .filter((step) => step.en.trim() || step.ar.trim())
                    .map((step) => step.ar.trim()),
                })),
              ))}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
